import { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Card,
  Table,
  Button,
  message,
} from "antd";
import { useNavigate } from "react-router";

import ProjectFilter from "../../components/ProjectFilter";
import DataState from "../../components/DataState/DataState";
import { PillPagination } from "../../components/PillPagination";
import { ProjectListHeader } from "./partials/ProjectListHeader";
import  {useProjectList}  from "../../hooks/projects/useProjectList";
import { deleteProject } from "../../services/projectService";
import {
  canManageProjects,
  getSessionRole,
  isProjectManagerRole,
  getProjectColumns,
  canDeleteProjects,
} from "./project.utils";
import { Role } from "../../config/rbac";

const { Title, Text } = Typography;

const PROJECTS_MENU_REFRESH_EVENT = "projects-menu-refresh";

const SORT_COLUMN_TO_API_FIELD = {
  projectId: "projectId",
  projectName: "projectDescription",
  customerName: "accountName",
  contractCategory: "contractCategory",
  startDate: "projectStartDate",
  endDate: "projectEndDate",
};

export default function ProjectList() {
  const navigate = useNavigate();
  const [messageApi, messageContextHolder] = message.useMessage();

  const sessionRole = getSessionRole();
  const isPm = isProjectManagerRole(sessionRole);

  const canAddProject =
    canManageProjects(sessionRole) || isPm;

  const canEditProject =
    canManageProjects(sessionRole) || isPm;

  const canDeleteProject =
    canDeleteProjects(sessionRole);

  // ---------------------------------------------
  // Delete state
  // ---------------------------------------------
  const [deletingId, setDeletingId] = useState(null);

  // ---------------------------------------------
  // Pagination state
  // ---------------------------------------------
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);


  const [sortCol, setSortCol] = useState("startDate");
  const [sortDir, setSortDir] = useState(1);

  const handleSort = useCallback(
    (col) => {
      if (sortCol === col) {
        setSortDir((d) => (d === 1 ? -1 : 1));
      } else {
        setSortCol(col);
        setSortDir(1);
      }
      setCurrentPage(1);
    },
    [sortCol],
  );

  // ---------------------------------------------
  // Filters
  // ---------------------------------------------
  const [appliedFilters, setAppliedFilters] =
    useState({
      search: "",
      statusFilter: undefined,
      flagFilter: undefined,
      deliveryManager: undefined,
      clientPartner: undefined,
      projectManager: undefined,
      contractCategory: undefined,
      businessArea: undefined,
      ragStatus: undefined,
      dateRangeType: undefined,
      dateRange: undefined,
    });

  // ---------------------------------------------
  // Project list query
  // ---------------------------------------------
  const {
    projects: projectsData,
    pagination,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useProjectList(
    currentPage - 1,
    pageSize,
    appliedFilters,
    SORT_COLUMN_TO_API_FIELD[sortCol] || "projectStartDate",
    sortDir === 1 ? "asc" : "desc",
  );

  const projects = projectsData || [];

  // Derived in the same render that starts a request (filter, page, sort, retry, refetch),
  // so no previously loaded rows are shown while a new request is in flight.
  const listStatus = isLoading || isFetching ? "loading" : error instanceof Error ? "error" : "ready";

  // ---------------------------------------------
  // Delete project handler
  // ---------------------------------------------
  const handleDeleteProject = useCallback(
    async (row) => {
      if (!row?.id && row?.id !== 0) {
        return;
      }

      setDeletingId(row.id);

      try {
        await deleteProject(row.id);

        messageApi.success(
          `Project "${row.projectName || row.projectId || row.id}" deleted successfully.`,
        );

        await refetch();
      } catch (deleteError) {
        messageApi.error(
          deleteError instanceof Error
            ? deleteError.message
            : "Failed to delete project. Please try again.",
        );
      } finally {
        setDeletingId(null);
      }
    },
    [messageApi, refetch],
  );

  // ---------------------------------------------
  // Project columns
  // ---------------------------------------------
  const columns = getProjectColumns({
    onView: (id) =>
      navigate(`/watchtower/projects/${id}`),

    onEdit: (id) =>
      navigate(`/watchtower/projects/${id}/edit`),

    canEdit: canEditProject,

    canEditRow: (row) => {
      if (!canEditProject) {
        return false;
      }

      return true;
    },

    onDelete: handleDeleteProject,

    canDelete: canDeleteProject,

    canDeleteRow: (row) => {
      if (!canDeleteProject) {
        return false;
      }

      return true;
    },

    deletingId,

    sortCol,
    sortDir,
    onSort: handleSort,
  });

  // ---------------------------------------------
  // Update total records
  // ---------------------------------------------
  useEffect(() => {
    const nextTotalRecords =
      pagination?.totalRecords;

    if (typeof nextTotalRecords === "number") {
      setTotalRecords(nextTotalRecords);
    }
  }, [
    pagination?.totalRecords,
  ]);

  // ---------------------------------------------
  // Validate current page
  // ---------------------------------------------
  useEffect(() => {
    const maxPage = Math.max(
      1,
      Math.ceil(totalRecords / pageSize),
    );

    if (currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [
    currentPage,
    pageSize,
    totalRecords,
  ]);

  // ---------------------------------------------
  // Refresh project menu
  // ---------------------------------------------
  useEffect(() => {
    const handleProjectsMenuRefresh = () => {
      const hasDefaultPagination =
        currentPage === 1 &&
        pageSize === 10;

      setPageSize(10);
      setCurrentPage(1);

      if (hasDefaultPagination) {
        void refetch();
      }
    };

    window.addEventListener(
      PROJECTS_MENU_REFRESH_EVENT,
      handleProjectsMenuRefresh,
    );

    return () => {
      window.removeEventListener(
        PROJECTS_MENU_REFRESH_EVENT,
        handleProjectsMenuRefresh,
      );
    };
  }, [
    currentPage,
    pageSize,
    refetch,
  ]);

  // ---------------------------------------------
  // Filter change
  // ---------------------------------------------
  const handleFilterChange = useCallback(
    (filterData) => {
      setCurrentPage(1);
      setAppliedFilters(filterData);
    },
    [],
  );

  // ---------------------------------------------
  // Render
  // ---------------------------------------------
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {messageContextHolder}

      {/* <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
        }}
      >
        <div style={{ flex: 1 }}>
          <ProjectFilter onFilterChange={handleFilterChange} />
        </div>
      </div> */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 16,
          width: "100%",
        }}
      >
        <div style={{ flex: "0 0 75%", display: listStatus !== "error" ? undefined : "none" }}>
          <ProjectFilter onFilterChange={handleFilterChange} />
        </div>
        <div style={{ display: listStatus !== "error" ? "contents" : "none" }}>
        {(sessionRole==Role.ADMIN || sessionRole==Role.PMO || sessionRole==Role.ACCOUNT_MANAGER) && (<div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <ProjectListHeader
            onAddProject={() => navigate("/watchtower/projects/new")}
            canAddProject={canAddProject}
          />
        </div>)}
        </div>
      </div>
      <DataState
        status={listStatus}
        errorTitle="Couldn't load projects"
        error={error instanceof Error ? error.message : undefined}
        onRetry={() => refetch()}
        skeletonRows={6}
      >
      <>

      {/* Table */}
      <Card
        style={{
          borderRadius: 14,
          border: "1px solid #e8edf5",
          boxShadow:
            "0 6px 18px rgba(20, 20, 20, 0.05)",
        }}
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <Table
          columns={columns}
          dataSource={projects}
          rowKey="id"
          tableLayout="fixed"
          style={{
            width: "100%",
            border: "1px solid #dbe3ef",
            borderRadius: 10,
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
          }}
          loading={isLoading}
          locale={{
            emptyText: (
              <div
                style={{
                  padding: "18px 0",
                }}
              >
                <Text
                  style={{
                    color: "#8c8c8c",
                  }}
                >
                  {error
                    ? "Unable to load projects."
                    : "No projects found."}
                </Text>
              </div>
            ),
          }}
          pagination={false}
          size="middle"
          onRow={(r, index) => ({
            style: {
              cursor: "pointer",
              background:
                index !== undefined &&
                  index % 2 === 0
                  ? "#fcfdff"
                  : "#ffffff",
              transition:
                "background 0.2s ease",
            },
            onClick: () => navigate(`/watchtower/projects/${r.id}`),
          })}
        />
      </Card>

      {/* Pagination */}
      <PillPagination
        currentPage={currentPage}
        pageSize={pageSize}
        total={totalRecords}
        itemLabel="projects"
        onPageChange={setCurrentPage}
        pageSizeOptions={[
          10,
          20,
          50,
          100,
        ]}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />
      </>
      </DataState>
    </div>
  );
}